import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiEdit3, FiInfo, FiPlus, FiRotateCcw, FiSearch, FiX } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import AddUserAccessRulePopup from "../popups/AddUserAccessRulePopup";
import { buildAccessTree, delegationLimits, LEVELS, scopeOf } from "../popups/itemAccessTree";
import PurchasingDocumentDialog from "../popups/PurchasingDocumentDialog";
import { getUserAccessMessages } from "../userAccessMessages";
import { getLocationName } from "../../utils/locationNames";
import { loadUserAccessPolicy } from "../../services/userAccessPolicy";
import "../../PurchasingUi.css";
import "./UserAccessRulesTab.css";

const EMPTY_EDITOR = { userId: null, rules: [], locationAccess: [] };
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
const isTreeRule = (rule) => Number(rule.policy_version) === 2;

function groupPolicies(rules, locations) {
  const grouped = new Map();
  const ensure = (row) => {
    const key = String(row.user_id);
    if (!grouped.has(key)) grouped.set(key, { key, userId: row.user_id, userName: "", userEmail: "", rules: [], locationAccess: [] });
    const policy = grouped.get(key);
    policy.userName = policy.userName || row.user?.name || "";
    policy.userEmail = policy.userEmail || row.user?.email || "";
    return policy;
  };
  rules.forEach((rule) => ensure(rule).rules.push(rule));
  locations.forEach((row) => ensure(row).locationAccess.push(row));
  return [...grouped.values()].map((policy) => {
    const tree = policy.rules.some(isTreeRule);
    const root = policy.rules.find((rule) => isTreeRule(rule) && !scopeOf(rule));
    const levels = LEVELS.flatMap(({ kind: relation, group: label }) => {
      const rows = policy.rules.filter((rule) => rule[`${relation}_id`]);
      // Keep legacy mixed modes separate instead of describing exclusions as inclusions.
      return [...new Set(rows.map((rule) => rule.rule_type))].map((mode) => ({
        key: `${relation}-${mode}`, label, mode,
        names: rows.filter((rule) => rule.rule_type === mode).map((rule) => rule[relation]?.name || `#${rule[`${relation}_id`]}`),
      }));
    });
    const unknownRules = policy.rules.some((rule) => {
      const scopes = LEVELS.filter(({ kind }) => rule[`${kind}_id`]);
      if (isTreeRule(rule)) return !["allow", "deny"].includes(rule.rule_type) || scopes.length > 1;
      if (rule.rule_type === "all") return scopes.length !== 0;
      return !["only", "all_except"].includes(rule.rule_type) || scopes.length !== 1 || Boolean(rule.item_id);
    });
    const hasLegacyBase = policy.rules.some((rule) => !isTreeRule(rule) && rule.rule_type !== "all");
    let badge = levels.length || unknownRules ? "restricted" : "allItems";
    if (tree && !hasLegacyBase && !unknownRules) {
      if (root?.rule_type === "deny") badge = levels.some(level => level.mode === "allow") ? "selectedOnly" : "noItems";
      else badge = levels.some(level => level.mode === "deny") ? "allByDefault" : "allItems";
    }
    return { ...policy, levels, unknownRules, tree, badge, restricted: badge !== "allItems" };
  }).sort((left, right) => left.userName.localeCompare(right.userName));
}

export default function UserAccessRulesTab() {
  const { themeId, languageId } = usePmsTheme();
  const words = getUserAccessMessages(languageId);
  const user = readStoredUser();
  const canView = hasPurchasingSettingsPermission(user, "access", "view");
  const canUpdate = canView && hasPurchasingSettingsPermission(user, "access", "update");
  const businessUnitId = user?.active_business_unit_id;
  const [data, setData] = useState({ rules: [], locations: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [editor, setEditor] = useState(null);
  const [resetPolicy, setResetPolicy] = useState(null);
  const [resetError, setResetError] = useState("");
  const [resetAllowed, setResetAllowed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const requestId = useRef(0);
  const resetInFlight = useRef(false);

  const fetchRules = useCallback(async ({ quiet = false } = {}) => {
    if (!canView) { setLoading(false); return false; }
    const currentRequest = ++requestId.current;
    if (quiet) setRefreshing(true); else setLoading(true);
    try {
      const policy = await loadUserAccessPolicy();
      if (currentRequest !== requestId.current) return false;
      setData(policy);
      setLoaded(true);
      setLoadError("");
      return true;
    } catch (error) {
      if (currentRequest === requestId.current) setLoadError(error.messageKey || "loadError");
      return false;
    } finally {
      if (currentRequest === requestId.current) { setLoading(false); setRefreshing(false); }
    }
  }, [canView]);

  useEffect(() => {
    setLoaded(false);
    setLoadError("");
    setData({ rules: [], locations: [] });
    setEditor(null);
    setResetPolicy(null);
    setNotice("");
    setExpanded(new Set());
    fetchRules();
    return () => { requestId.current += 1; };
  }, [businessUnitId, fetchRules]);

  useEffect(() => {
    const onFocus = () => { if (!editor && !resetPolicy) fetchRules({ quiet: true }); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [editor, resetPolicy, fetchRules]);

  useEffect(() => {
    if (!resetPolicy) return undefined;
    let active = true;
    async function checkReset() {
      try {
        const { data: catalog } = await axios.get("/api/purchasing/user-item-access-rules/context", { params: { business_unit_id: businessUnitId } });
        if (!active) return;
        if (!Array.isArray(catalog?.delegation_rules)) throw new Error("Missing delegation scope");
        const ceiling = buildAccessTree(catalog, catalog.delegation_rules);
        if (ceiling.unsupported) throw new Error("Unsupported delegation scope");
        if (resetPolicy.rules.length && delegationLimits(ceiling, ceiling).restricted) setResetError("resetOutsideScope");
        else setResetAllowed(true);
      } catch {
        if (active) setResetError("resetScopeUnavailable");
      }
    }
    checkReset();
    return () => { active = false; };
  }, [resetPolicy, businessUnitId]);

  const policies = useMemo(() => groupPolicies(data.rules, data.locations), [data]);
  const visible = useMemo(() => {
    const search = normalize(query.trim());
    return policies.filter((policy) => !search || normalize([
      policy.userName, policy.userEmail, words.userId(policy.userId),
      words[policy.badge], ...policy.levels.flatMap((level) => [words[level.label], words[level.mode], ...level.names]),
      ...policy.locationAccess.flatMap((row) => [row.location?.name, row.location?.code]),
    ].join(" ")).includes(search));
  }, [policies, query, words]);
  const writeDisabled = loading || refreshing || Boolean(loadError) || !loaded || resetting || data.migrationRequired;

  const openEditor = (policy) => {
    if (!canUpdate || writeDisabled) return;
    setNotice("");
    setEditor(policy ? { userId: policy.userId, rules: policy.rules, locationAccess: policy.locationAccess } : EMPTY_EDITOR);
  };
  const resetAccess = async () => {
    if (!resetPolicy || !resetAllowed || !canUpdate || writeDisabled || resetInFlight.current) return;
    resetInFlight.current = true;
    setResetting(true);
    setResetError("");
    setNotice("");
    try {
      await axios.delete(`/api/purchasing/user-item-access-rules/user/${resetPolicy.userId}?business_unit_id=${businessUnitId}${resetPolicy.tree ? "&policy_version=2" : ""}`);
      setResetPolicy(null);
      setNotice("resetSuccess");
      await fetchRules({ quiet: true });
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === "ITEM_ACCESS_DELEGATION_DENIED") {
        setResetError("resetOutsideScope"); setResetAllowed(false);
      } else setResetError(code === "ITEM_ACCESS_CATALOG_BUSY" ? "catalogBusy" : "resetError");
    } finally {
      resetInFlight.current = false;
      setResetting(false);
    }
  };

  return <section className="pilcore-purchasing-ui uar-shell" data-theme={themeId} aria-labelledby="uar-title">
    <header className="uar-header">
      <div><h2 id="uar-title">{words.title}</h2><p>{words.description}</p></div>
      {canView && canUpdate && <button type="button" className="uar-button uar-button--primary" disabled={writeDisabled} onClick={() => openEditor()}>
        <FiPlus aria-hidden="true" />{words.configure}
      </button>}
    </header>
    {!canView ? <p className="uar-empty" role="alert">{words.noPermission}</p> : <>
      <div className="uar-guidance"><FiInfo aria-hidden="true" /><span>{words.defaults}</span></div>
      {!canUpdate && <p className="uar-notice" role="status">{words.readonly}</p>}
      {data.migrationRequired && !loadError && <p className="uar-notice" role="status">{words.migrationRequired}</p>}
      {loadError && <div className="uar-notice uar-notice--error" role="alert">
        <span>{words[loadError]}</span><button type="button" className="uar-button" disabled={refreshing || loading} onClick={() => fetchRules({ quiet: loaded })}>{words.retry}</button>
      </div>}
      {notice && <p className="uar-notice uar-notice--success" role="status">{words[notice]}</p>}
      <div className="uar-toolbar">
        <label className="uar-search"><FiSearch aria-hidden="true" />
          <input className="form-control" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={words.search} aria-label={words.search} />
        </label>
        {query && <button type="button" className="uar-button" onClick={() => setQuery("")} aria-label={words.clearSearch}><FiX aria-hidden="true" /></button>}
        <span className="uar-count">{words.count(visible.length, policies.length)}</span>
        {refreshing && <span className="uar-sync" role="status">{words.syncing}</span>}
      </div>
      {loading && !loaded ? <p className="uar-empty" role="status">{words.loading}</p> : loaded && <div className="uar-table-scroll" tabIndex={0} aria-label={words.title}>
        <table className="uar-table">
          <caption className="uar-sr-only">{words.title}</caption>
          <colgroup><col className="uar-col-user" /><col className="uar-col-items" /><col className="uar-col-locations" /><col className="uar-col-actions" /></colgroup>
          <thead><tr>{[words.user, words.items, words.locations, words.actions].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{visible.map((policy) => {
            const name = policy.userName || words.userId(policy.userId);
            const isExpanded = expanded.has(policy.key);
            const hasDisabledCapabilities = policy.locationAccess.some((row) => ["can_receive", "can_count", "can_dispatch", "can_transfer"].some((key) => row[key] === false));
            const locationRows = [...policy.locationAccess].sort((a, b) => Number(Boolean(b.is_default)) - Number(Boolean(a.is_default)));
            return <React.Fragment key={policy.key}>
              <tr className={isExpanded ? "uar-row--expanded" : undefined}>
                <th scope="row"><div className="uar-user">
                  <span className="uar-avatar" aria-hidden="true">{name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("")}</span>
                  <div><strong>{name}</strong><small>{policy.userEmail || words.userId(policy.userId)}</small></div>
                </div></th>
                <td><span className={`uar-badge ${policy.restricted ? "uar-badge--scoped" : ""}`}>{words[policy.badge]}</span>
                  {policy.levels.slice(0, 2).map((level) => <div className="uar-scope-preview" key={level.key}>
                    <strong>{level.names.length} · {words[level.label]} · {words[level.mode] || words.mixed}</strong> · {level.names.slice(0, 2).join(", ")}{level.names.length > 2 ? ` (+${level.names.length - 2})` : ""}
                  </div>)}
                  {policy.levels.length > 2 && <small className="uar-muted">{policy.levels.slice(2).map(level =>
                    `${level.names.length} ${words[level.label]} · ${words[level.mode] || words.mixed}`).join(" / ")}</small>}
                  {policy.unknownRules && <small className="uar-warning">{words.unknown}</small>}
                </td>
                <td>{locationRows.length ? <div className="uar-locations">{locationRows.slice(0, 2).map((row) => <span className={`uar-badge ${row.is_default ? "uar-badge--scoped" : ""}`} key={row.id || row.location_id}>
                  {getLocationName(row.location, languageId)}{row.is_default && <small>{words.default}</small>}
                </span>)}{locationRows.length > 2 && <span className="uar-muted">+{locationRows.length - 2}</span>}</div> : <span className="uar-muted">{words.allLocations}</span>}</td>
                <td><div className="uar-row-actions">
                  {canUpdate && <button type="button" className="uar-button" disabled={writeDisabled} aria-label={`${words.edit}: ${name}`} onClick={() => openEditor(policy)}><FiEdit3 aria-hidden="true" />{words.edit}</button>}
                  <button type="button" className="uar-detail-toggle" aria-expanded={isExpanded} aria-controls={`uar-detail-${policy.key}`} aria-label={`${isExpanded ? words.hideDetails : words.showDetails}: ${name}`} onClick={() => setExpanded((current) => {
                    const next = new Set(current); if (next.has(policy.key)) next.delete(policy.key); else next.add(policy.key); return next;
                  })}>{isExpanded ? words.hideDetails : words.showDetails}<FiChevronDown aria-hidden="true" /></button>
                </div></td>
              </tr>
              <tr id={`uar-detail-${policy.key}`} hidden={!isExpanded} className="uar-details-row"><td colSpan={4}>
                {isExpanded && <div className="uar-details">
                  <div><h3>{words.items}</h3><p>{policy.tree ? words.treeHelp : words.ruleHelp}</p>
                    <span>{words[policy.badge]}</span>
                    {policy.levels.map((level) => <p key={level.key}><strong>{words[level.label]} · {words[level.mode] || words.mixed}: </strong>{level.names.join(", ")}</p>)}
                    {policy.unknownRules && <p className="uar-warning">{words.unknown}</p>}
                  </div>
                  <div><h3>{words.locations}</h3><p>{words.locationHelp}</p>
                    {locationRows.length ? <ul>{locationRows.map((row) => <li key={row.id || row.location_id}>{getLocationName(row.location, languageId)}{row.is_default ? ` · ${words.default}` : ""}</li>)}</ul> : <span>{words.allLocations}</span>}
                    {hasDisabledCapabilities && <p className="uar-warning">{words.capabilityCaution}</p>}
                  </div>
                  {canUpdate && <div className="uar-reset"><p>{words.resetWarning}</p><button type="button" className="uar-button uar-button--danger" disabled={writeDisabled} aria-label={`${words.reset}: ${name}`} onClick={() => { setResetError(""); setResetAllowed(false); setResetPolicy(policy); }}><FiRotateCcw aria-hidden="true" />{words.reset}</button></div>}
                </div>}
              </td></tr>
            </React.Fragment>;
          })}
          {!visible.length && <tr><td colSpan={4} className="uar-empty"><strong>{policies.length ? words.noMatches : words.empty}</strong>{!policies.length && <p>{words.emptyHelp}</p>}</td></tr>}
          </tbody>
        </table>
      </div>}
    </>}
    <AddUserAccessRulePopup isOpen={canView && canUpdate && Boolean(editor)} onClose={() => setEditor(null)}
      onAdded={async () => { setNotice("saved"); await fetchRules({ quiet: true }); }}
      initialUserId={editor?.userId ?? null} initialRules={editor?.rules || EMPTY_EDITOR.rules} initialLocationAccess={editor?.locationAccess || EMPTY_EDITOR.locationAccess} />
    <PurchasingDocumentDialog isOpen={canView && canUpdate && Boolean(resetPolicy)} title={words.resetTitle}
      message={<><strong>{resetPolicy?.userName || words.userId(resetPolicy?.userId)}</strong><p>{words.resetWarning}</p>{resetError && <p role="alert" className="uar-reset-error">{words[resetError]}</p>}</>}
      confirmLabel={resetting ? words.resetting : words.reset} cancelLabel={words.cancel} tone="warning" isBusy={resetting} confirmDisabled={!resetAllowed || (writeDisabled && !resetting)}
      onConfirm={resetAccess} onClose={() => { if (!resetInFlight.current) setResetPolicy(null); }} />
  </section>;
}
